// Pure helpers for dimension-driven movement of selected gridlines in the
// frame editor (Item2dEditor). Positions are absolute inches, as produced
// by gridlinePositions(); index 0 and the last index are the
// perimeter-coincident lines, which get no special treatment here.
// Zero imports so this runs under `node --test` directly.

// Stable ascending sort. `remap` maps each entry's old index to its new
// index, so callers can keep a selection pointing at the same physical
// lines after a crossing renumbers them.
export function sortPositionsWithRemap(positions) {
  const order = positions.map((p, i) => ({ p, i })).sort((a, b) => a.p - b.p);
  const remap = new Map();
  order.forEach((e, newIndex) => remap.set(e.i, newIndex));
  return { positions: order.map((e) => e.p), remap };
}

// Returns an object that behaves like `dim` but whose `callback` is
// replaced. Drawing dim entities (AlignedDim) are class instances whose
// `value` is a prototype getter, so a `{...dim}` spread would silently
// drop it; delegating through the prototype chain keeps every property
// and getter intact while leaving the original entity untouched.
export function withWrappedCallback(dim, callback) {
  return Object.create(dim, {
    callback: { value: callback, enumerable: true, configurable: true },
  });
}

// The new dimension-edit case: exactly one endpoint of the edited gap is
// selected. Translates every selected line so the selected endpoint sits
// `newValueInches` from the unselected endpoint, preserving spacing within
// the selection. Unselected lines never move; the group may pass them.
// Returns null whenever the edit is NOT this case — the caller falls back
// to the existing single-gridline callback.
export function translateGridlineGroup({
  positions,
  gapIndex,
  selectedIndices,
  newValueInches,
}) {
  if (gapIndex < 0 || gapIndex + 1 >= positions.length) return null;

  const selected = new Set(selectedIndices);
  const leftSelected = selected.has(gapIndex);
  const rightSelected = selected.has(gapIndex + 1);
  if (leftSelected === rightSelected) return null;

  const value = Math.max(newValueInches, 0);
  const current = positions[gapIndex + 1] - positions[gapIndex];
  const delta = rightSelected ? value - current : current - value;

  const moved = positions.map((p, i) => (selected.has(i) ? p + delta : p));
  return sortPositionsWithRemap(moved);
}
